import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import { Badge } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { scannerCaseNotificationCountTemplate, triggerScannerCaseSyncTemplate } from 'apollo/templates/scanner-case';
import WifiIcon from '@mui/icons-material/Wifi';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import SyncLockIcon from '@mui/icons-material/SyncLock';
import { CaseListSourceStatus, CaseListSourceStatuses } from 'models/scanner-case';
import { useNavigate } from 'react-router-dom';
import { socketIo } from 'context/socket';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

const ScannerCaseNotifications: FC = () => {
    const [caseCount, setCaseCount] = useState(0);
    const [statuses, setStatuses] = useState<CaseListSourceStatuses>();
    const navigate = useNavigate();
    const socket = useContext(socketIo.getSocketContext());
    const organization = currentOrgVar();

    const { refetch } = useQuery(scannerCaseNotificationCountTemplate, {
        fetchPolicy: 'network-only',
        pollInterval: 1 * 60 * 1000,
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ scannerCaseNotificationCount }) => {
            setCaseCount(scannerCaseNotificationCount);
            triggerScannerCaseSync();
        },
        onError(error) {
            console.log(error);
        },
    });

    const [triggerScannerCaseSync] = useMutation(triggerScannerCaseSyncTemplate, {
        onCompleted: ({ triggerScannerCaseSync }) => {
            setStatuses(triggerScannerCaseSync);
        },
        onError: (error) => {
            console.log(error);
        },
    });

    useEffect(() => {
        socket.on('SCAN_CASE_SOURCE_STATUS_UPDATED', (event) => {
            if (event.data.organization === organization._id) {
                refetch();
            }
        });

        return () => {
            socket.removeAllListeners('SCAN_CASE_SOURCE_STATUS_UPDATED');
        };
    }, [socket]);

    const notificationIconButton = () => {
        let hasErrorStatus = false;
        let hasNotAuthenticatedStatus = false;
        if (statuses) {
            hasErrorStatus = Object.values(statuses).some(
                (status: any) => status && status.status === CaseListSourceStatus.ERROR,
            );
            hasNotAuthenticatedStatus = Object.values(statuses).some(
                (status: any) => status && status.status === CaseListSourceStatus.NOT_AUTHENTICATED,
            );
        }

        if (hasErrorStatus) {
            return (
                <IconButton
                    edge="end"
                    onClick={() => navigate('/orders/scanner-cases')}
                    color="inherit"
                    size="large"
                    sx={{ color: 'error.main', mr: 0.5 }}
                    data-test="main-app-bar.scanner-case-btn"
                >
                    <Badge badgeContent={caseCount} color="error">
                        <SyncProblemIcon />
                    </Badge>
                </IconButton>
            );
        }

        if (hasNotAuthenticatedStatus) {
            return (
                <IconButton
                    edge="end"
                    onClick={() => navigate('/orders/scanner-cases')}
                    color="inherit"
                    size="large"
                    sx={{ color: 'warning.main', mr: 0.5 }}
                    data-test="main-app-bar.scanner-case-btn"
                >
                    <Badge badgeContent={caseCount} color="error">
                        <SyncLockIcon />
                    </Badge>
                </IconButton>
            );
        }

        return (
            <IconButton
                edge="end"
                onClick={() => navigate('/orders/scanner-cases')}
                color="inherit"
                size="large"
                sx={{ color: '#fff', mr: 0.5 }}
                data-test="main-app-bar.scanner-case-btn"
            >
                <Badge badgeContent={caseCount} color="error">
                    <WifiIcon />
                </Badge>
            </IconButton>
        );
    };

    return <Fragment>{notificationIconButton()}</Fragment>;
};

export default ScannerCaseNotifications;
